/**
 * Centralized severity mapping utilities for Enterprise CortexFlow SPA.
 *
 * This module provides DRY-compliant mapping functions between different
 * severity/level systems used throughout the application.
 */

import {
    ApplicationInsightsSeverity,
    LogLevel,
    NotificationSeverity,
    UnifiedLevel
} from '../types/severity';

// ========== Primary Mapping Functions ==========

/**
 * Maps NotificationSeverity from backend to UnifiedLevel for UI display.
 * Handles both enum and string inputs for compatibility during transitions.
 *
 * @param severity - Backend notification severity
 * @returns Unified level for consistent UI display
 */
export function mapNotificationSeverityToLevel(
  severity: NotificationSeverity | string
): UnifiedLevel {
  switch (severity) {
    case 'critical':
    case 'error':
      return 'error';
    case 'warning':
      return 'warning';
    case 'info':
    case 'healthy':
      return 'info';
    default:
      // Fallback for unknown severities
      console.warn(`Unknown notification severity: ${severity}, defaulting to 'info'`);
      return 'info';
  }
}

/**
 * Maps UnifiedLevel to Application Insights severity numbers.
 * Replaces the mapLogLevel function in ApplicationInsightsTransport.
 *
 * @param level - Unified level
 * @returns Application Insights severity number
 */
export function mapLevelToApplicationInsights(level: UnifiedLevel): ApplicationInsightsSeverity {
  switch (level) {
    case 'debug':
      return 0; // Verbose
    case 'info':
      return 1; // Information
    case 'warning':
      return 2; // Warning
    case 'error':
      return 3; // Error
    default:
      // TypeScript exhaustiveness check - should never reach here
      {
        const _exhaustive: never = level;
        console.warn(`Unknown level: ${_exhaustive}, defaulting to Information`);
      }
      return 1;
  }
}

// ========== Legacy Compatibility Functions ==========

/**
 * @deprecated Use mapNotificationSeverityToLevel instead
 * Legacy function for backward compatibility during migration.
 */
export function mapSeverityToLevel(severity: string): 'info' | 'warning' | 'error' {
  const level = mapNotificationSeverityToLevel(severity);
  // Filter out 'debug' which doesn't exist in legacy SystemEventLevel
  return level === 'debug' ? 'info' : level as 'info' | 'warning' | 'error';
}

/**
 * @deprecated Use mapLevelToApplicationInsights instead
 * Legacy function for backward compatibility during migration.
 */
export function mapLogLevel(level: LogLevel | string): ApplicationInsightsSeverity {
  // Handle legacy 'warn' -> 'warning' conversion
  const normalizedLevel = level === 'warn' ? 'warning' : level;
  return mapLevelToApplicationInsights(normalizedLevel as UnifiedLevel);
}

// ========== Utility Functions ==========

/**
 * Normalizes legacy LogLevel to UnifiedLevel.
 * Handles the 'warn' vs 'warning' inconsistency.
 *
 * @param level - Legacy log level
 * @returns Normalized unified level
 */
export function normalizeLogLevel(level: LogLevel): UnifiedLevel {
  return level === 'warn' ? 'warning' : level as UnifiedLevel;
}

/**
 * Checks if a level represents an error condition.
 *
 * @param level - Any level type
 * @returns True if level indicates error/critical condition
 */
export function isErrorLevel(level: UnifiedLevel | NotificationSeverity): boolean {
  return level === 'error' || level === 'critical';
}

/**
 * Checks if a level represents a warning condition.
 *
 * @param level - Any level type
 * @returns True if level indicates warning condition
 */
export function isWarningLevel(level: UnifiedLevel | NotificationSeverity): boolean {
  return level === 'warning';
}

/**
 * Gets display priority for sorting levels (higher = more severe).
 *
 * @param level - Unified level
 * @returns Priority number for sorting
 */
export function getLevelPriority(level: UnifiedLevel): number {
  switch (level) {
    case 'error':
      return 3;
    case 'warning':
      return 2;
    case 'info':
      return 1;
    case 'debug':
      return 0;
    default:
      // TypeScript exhaustiveness check - should never reach here
      return 0;
  }
}
